import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";
import { useState } from "react";
import Dropzone from "react-dropzone";
import { useSpinDelay } from "spin-delay";
import CustomerList from "./CustomerList";
import ExistingCustomerList from "./ExistingCustomerList";
import UpdatedActiveCustomerList from "./UpdatedActiveCustomerList";
import {
  BaseUpdate,
  BaseValidateResponse,
  DropzoneWrapper,
  EditableBaseUpdate,
  ItemTitle,
  LoadingWrapper,
  PageWrapper,
  SaveResponse,
} from "../common/visma";
import PluralText, { PluralData } from "../common/PluralText";

export interface Props {
  urls: {
    validate: string;
    save: string;
  };
}

export interface VismaCustomer {
  customerNo: string | null;
  name: string | null;
  address1: string | null;
  address2: string | null;
  zipCode: string | null;
  town: string | null;
  contactPhone: string | null;
  contactPerson: string | null;
  email: string | null;
  organizationNumber: string | null;
  reference: string | null;
}

export enum ValueType {
  None = 0,
  Name = 1 << 0,
  Address1 = 1 << 1,
  Address2 = 1 << 2,
  ZipCode = 1 << 3,
  Town = 1 << 4,
  ContactPhone = 1 << 5,
  ContactPerson = 1 << 6,
  Email = 1 << 7,
  OrganizationNumber = 1 << 8,
  Reference = 1 << 9,
}

export interface CustomerDelta {
  new: VismaCustomer[];
  updates: Update[];
  updatedActiveStates: UpdatedActiveState[];
}

export interface UpdatedActiveState {
  customerNo: string;
  name: string;
  active: boolean;
}

export type Update = BaseUpdate<VismaCustomer, ValueType>;
export type EditableUpdate = EditableBaseUpdate<VismaCustomer, ValueType>;
export type ValidateResponse = BaseValidateResponse<CustomerDelta>;

enum PageState {
  WaitingForFileInput,
  ValidatingFile,
  WaitingForSaveInput,
  SavingFile,
  Saved,
}

const ImportCustomersPage = ({ urls }: Props) => {
  const [pageState, setPageState] = useState(PageState.WaitingForFileInput);
  const [validateResponse, setValidateResponse] = useState(
    null as ValidateResponse | null
  );
  const [saveErrorMessage, setSaveErrorMessage] = useState(
    null as string | null
  );
  const [customerUpdates, setCustomerUpdates] = useState(
    null as EditableUpdate[] | null
  );
  const showValidationLoading = useSpinDelay(
    pageState === PageState.ValidatingFile
  );
  const showSaveLoading = useSpinDelay(pageState === PageState.SavingFile);

  const onFileSelected = async (files) => {
    let file = files[0];

    if (file == null) {
      return;
    }

    setPageState(PageState.ValidatingFile);
    const data = new FormData();
    data.append("file", file);

    const response = await fetch(urls.validate, {
      method: "POST",
      body: data,
    });

    const responseData = (await response.json()) as ValidateResponse;
    setValidateResponse(responseData);

    if (responseData.success) {
      const newCustomerUpdates = [] as EditableUpdate[];
      for (let i = 0; i < responseData.delta.updates.length; ++i) {
        let update = responseData.delta.updates[i];
        newCustomerUpdates.push({
          ...update,
          overwrites: update.conflicts,
        });
      }

      setCustomerUpdates(newCustomerUpdates);
      setPageState(PageState.WaitingForSaveInput);
    } else {
      setPageState(PageState.WaitingForFileInput);
    }
  };

  const saveImport = async () => {
    if (customerUpdates == null) {
      return;
    }

    setPageState(PageState.SavingFile);

    const overwriteOptions = {
      overwrites: Object.fromEntries(
        customerUpdates.map((upd) => {
          let overwrites = upd.changes;
          overwrites &= ~upd.conflicts; // remove conflicts from overwrites
          overwrites |= upd.overwrites; // add overwrites set by user
          return [upd.new.customerNo, overwrites];
        })
      ),
    };

    const response = await fetch(
      urls.save + `?importId=${validateResponse?.saveImportId}`,
      {
        method: "POST",
        body: JSON.stringify(overwriteOptions),
        headers: [["Content-Type", "application/json"]],
      }
    );

    let responseData = (await response.json()) as SaveResponse;
    setSaveErrorMessage(responseData.message);

    if (responseData.success) {
      setPageState(PageState.Saved);
      setTimeout(() => {
        document.location.href = responseData.redirectUrl;
      }, 3000);
    } else {
      setPageState(PageState.WaitingForSaveInput);
    }
  };

  function onSetFieldOverwrite(
    customer: EditableUpdate,
    newOverwriteFlags: ValueType
  ) {
    setCustomerUpdates(
      customerUpdates?.map((mapCustomer) => {
        if (mapCustomer.new.customerNo === customer.new.customerNo) {
          return {
            ...mapCustomer,
            overwrites: newOverwriteFlags,
          };
        }

        return mapCustomer;
      }) ?? null
    );
  }

  const renderPageState = (): React.ReactNode => {
    switch (pageState) {
      case PageState.WaitingForFileInput:
      case PageState.ValidatingFile: {
        return (
          <div>
            <Dropzone onDrop={onFileSelected} multiple={false}>
              {({ getRootProps, getInputProps }) => (
                <DropzoneWrapper>
                  <div {...getRootProps()}>
                    <input {...getInputProps()} />
                    <span>
                      Klicka eller släpp en fil här för att importera från Visma
                      Administration
                    </span>
                  </div>
                </DropzoneWrapper>
              )}
            </Dropzone>
            {validateResponse?.message && (
              <span className="error-message">{validateResponse.message}</span>
            )}
          </div>
        );
      }
      case PageState.WaitingForSaveInput:
      case PageState.SavingFile: {
        const numNewCustomers = validateResponse?.delta.new.length;
        const numUpdatedCustomers = validateResponse?.delta.updates.length;
        const numUpdatedActiveStateCustomers =
          validateResponse?.delta.updatedActiveStates.length;
        const pluralData: PluralData = {
          singular: "kund",
          plural: "kunder",
        };
        return (
          <div>
            <ItemTitle>Nya kunder</ItemTitle>
            <CustomerList customers={validateResponse!.delta.new} />
            <ItemTitle>Kunder med nya värden</ItemTitle>
            <ExistingCustomerList
              customers={customerUpdates!}
              onSetFieldOverwrite={onSetFieldOverwrite}
            />
            <ItemTitle>Kunder med ny aktiv status</ItemTitle>
            <UpdatedActiveCustomerList
              customers={validateResponse?.delta.updatedActiveStates ?? []}
            />
            <ItemTitle>Sammanfattning</ItemTitle>
            {!numNewCustomers &&
            !numUpdatedCustomers &&
            !numUpdatedActiveStateCustomers ? (
              <p>Hittade ingen ny data att importera.</p>
            ) : null}
            {numNewCustomers ||
            numUpdatedCustomers ||
            numUpdatedActiveStateCustomers ? (
              <>
                <ul>
                  <li>
                    {numNewCustomers} nya{" "}
                    <PluralText count={numNewCustomers} data={pluralData} />
                  </li>
                  <li>
                    {numUpdatedCustomers} existerande{" "}
                    <PluralText count={numUpdatedCustomers} data={pluralData} />{" "}
                    med nya värden
                  </li>
                  <li>
                    {numUpdatedActiveStateCustomers}{" "}
                    <PluralText
                      count={numUpdatedActiveStateCustomers}
                      data={pluralData}
                    />{" "}
                    med ny aktiv status.
                  </li>
                </ul>
                <button
                  className="button primary"
                  onClick={saveImport}
                  disabled={pageState == PageState.SavingFile}
                >
                  Importera
                </button>
                {saveErrorMessage?.length && (
                  <span className="error-message">{saveErrorMessage}</span>
                )}
              </>
            ) : null}
          </div>
        );
      }
      case PageState.Saved: {
        return (
          <LoadingWrapper>
            Importeringen slutfördes utan problem. Du skickas tillbaka till
            kundlistan om 3 sekunder.
          </LoadingWrapper>
        );
      }
      default:
        throw new Error(`Unhandled PageState: ${pageState}`);
    }
  };

  return (
    <PageWrapper>
      {renderPageState()}

      {showValidationLoading && (
        <LoadingWrapper>
          <FontAwesomeIcon icon={faSpinner} spin={true} size="4x" />
          Vänta medan filens innehåll kontrolleras och jämförs med existerande
          värden...
        </LoadingWrapper>
      )}
      {showSaveLoading && (
        <LoadingWrapper>
          <FontAwesomeIcon icon={faSpinner} spin={true} size="4x" />
          Vänta medan företagen importeras...
        </LoadingWrapper>
      )}
    </PageWrapper>
  );
};

export default ImportCustomersPage;
